import * as React from 'react';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { Loader } from 'Src/components/Loader/Loader';
import './SchoolModules.scss';
import {
  getSchoolModules,
  createModuleSubscriptionRequest,
  ModuleSubscription
} from 'Src/helpers/service/admin/schoolModules';

interface Props {
  user: AppUser;
}

const MODULES_CONFIG: Record<string, { title: string; description: string }> = {
  SPORT: {
    title: 'Sport',
    description: 'Fixtures, teams, and competitions management.'
  },
  CHALLENGES: {
    title: 'Challenges',
    description: 'Whole-school activity challenges and leaderboards.'
  },
  LEAGUES: {
    title: 'Leagues & Competitions',
    description: 'Internal school leagues and competitions.'
  },
  CLUBS: {
    title: 'Clubs & Activities',
    description: 'After-school clubs and activities, bookings and waiting lists.'
  },
  COMMUNICATIONS: {
    title: 'Whole-school communications',
    description: 'Messages, notifications and announcements.'
  },
  AWARDS: {
    title: 'Awards & House Points',
    description: 'Custom awards, house race and leaderboards.'
  },
  TOURNAMENTS: {
    title: 'Tournaments',
    description: 'Multi-school tournaments and competitions management.'
  },
  MIS: {
    title: 'MIS integration',
    description: 'Automatic sync of students, staff, classes and groups.'
  }
};

const MODULE_ORDER = ['SPORT', 'CHALLENGES', 'LEAGUES', 'CLUBS', 'AWARDS', 'COMMUNICATIONS', 'TOURNAMENTS', 'MIS'];

const ALLOWED_MODULE_KEYS = new Set(MODULE_ORDER);

function toDate(input?: string | Date | null): Date | null {
  if (!input) return null;
  if (input instanceof Date) return input;
  const d = new Date(input);
  return isNaN(d.getTime()) ? null : d;
}

function formatDate(d?: string | Date | null): string {
  const date = toDate(d);
  if (!date) return '—';

  const dayNum = date.getDate();
  const monthNum = date.getMonth() + 1;
  const year = date.getFullYear();

  const day = (dayNum < 10 ? '0' : '') + dayNum;
  const month = (monthNum < 10 ? '0' : '') + monthNum;

  return `${day}.${month}.${year}`;
}

function getNextDay(date: Date | null): Date | null {
  if (!date) return null;
  const d = new Date(date.getTime());
  d.setDate(d.getDate() + 1);
  return d;
}

const buildStatusLabel = (sub: ModuleSubscription) => {
  const status = sub.status as ModuleSubscription['status'] | 'PENDING';
  const validUntil = sub.validUntil ? new Date(sub.validUntil) : null;

  const trialEverActivated = sub.trialEverActivated;
  const trialLastEndedAt = sub.trialLastEndedAt ? new Date(sub.trialLastEndedAt) : null;
  const isTrialAvailable = sub.isTrialAvailable;

  const formatShort = (d: Date | null) => (d ? formatDate(d) : '');

  let pillKind: 'active' | 'inactive' | 'trial' | 'pending' = 'inactive';
  let pillText = '';
  const metaLines: string[] = [];

  if (status === 'ACTIVE' || status === 'ACTIVE_FULL' || status === 'ACTIVE_INCLUDED') {
    pillKind = 'active';
    pillText = 'Active';

    if (sub.includedWithModule) {
      metaLines.push(`Included with ${sub.includedWithModule.toLowerCase()} module`);
    }

    const renewalDate = getNextDay(validUntil);
    if (renewalDate) {
      metaLines.push(`Renewal: ${formatShort(renewalDate)}`);
    }
  } else if (status === 'TRIAL' || status === 'ACTIVE_TRIAL') {
    pillKind = 'trial';
    pillText = 'Trial active';

    if (validUntil) {
      metaLines.push(`Trial until ${formatShort(validUntil)}`);
    }
  } else if (status === 'PENDING') {
    pillKind = 'pending';
    pillText = 'Pending';
  } else {
    pillKind = 'inactive';
    pillText = 'Not active';

    if (canRequestTrial(sub)) {
      metaLines.push('Trial available');
    } else if (trialEverActivated && trialLastEndedAt) {
      metaLines.push(`Trial ended on ${formatShort(trialLastEndedAt)}`);
    }
  }

  return { kind: pillKind, text: pillText, metaLines };
};

function getLastRequestText(sub: ModuleSubscription): string | undefined {
  const requests = sub.requests || [];
  if (!requests.length) return undefined;

  const last = requests[requests.length - 1];
  const date = last.requestedAt ? formatDate(last.requestedAt) : '';

  let typeText: string;

  switch (last.type) {
    case 'TRIAL':
      typeText = 'Trial activation';
      break;
    case 'FULL':
      typeText = 'Full subscription';
      break;
    default:
      typeText = 'Request';
  }

  const statusText = last.status.toLowerCase();

  if (date) {
    return `Last request: ${typeText} – ${statusText} (${date})`;
  }
  return `Last request: ${typeText} – ${statusText}`;
}

function canRequestTrial(sub: ModuleSubscription): boolean {
  if (!sub.isTrialAvailable) return false;
  if (getRowState(sub) !== 'inactive') return false;
  if (sub.module === 'CHALLENGES' || sub.module === 'LEAGUES' || sub.module === 'TOURNAMENTS') {
    return false;
  }
  const last = (sub.requests || []).slice(-1)[0];
  if (last && last.status === 'PENDING' && last.type === 'TRIAL') {
    return false;
  }
  return true;
}

function canRequestActivation(sub: ModuleSubscription): boolean {
  if (getRowState(sub) === 'active') return false;
  if (sub.module === 'CHALLENGES' || sub.module === 'LEAGUES' || sub.module === 'TOURNAMENTS') {
    return false;
  }
  const last = (sub.requests || []).slice(-1)[0];
  if (last && last.status === 'PENDING' && last.type === 'FULL') {
    return false;
  }
  return true;
}

type RowState = 'active' | 'trial' | 'inactive';
type RequestMode = 'TRIAL' | 'FULL';

function getRowState(sub: ModuleSubscription): RowState {
  const st = sub.status;

  if (st === 'ACTIVE' || st === 'ACTIVE_FULL' || st === 'ACTIVE_INCLUDED') {
    return 'active';
  }
  if (st === 'TRIAL' || st === 'ACTIVE_TRIAL') {
    return 'trial';
  }
  return 'inactive';
}

export const SchoolModules: React.FunctionComponent<Props> = props => {
  const { user } = props;
  const school = propz.get(user, ['activeSchool']);
  const schoolName = propz.get(school, ['name'], 'your school');

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [subscriptions, setSubscriptions] = React.useState<ModuleSubscription[]>([]);
  const [submittingModule, setSubmittingModule] = React.useState<string | null>(null);
  const [infoMessage, setInfoMessage] = React.useState<string | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const [pendingRequest, setPendingRequest] = React.useState<{
    moduleKey: string;
    mode: RequestMode;
    moduleTitle: string;
  } | null>(null);

  const [requestMessage, setRequestMessage] = React.useState<string>('');

  const composerRef = React.useRef<HTMLDivElement | null>(null);
  const composerTextareaRef = React.useRef<HTMLTextAreaElement | null>(null);

  const loadModules = React.useCallback(() => {
    setIsLoading(true);
    setErrorMessage(null);

    getSchoolModules(user)
      .then(mods => {
        setSubscriptions(mods || []);
      })
      .catch(() => {
        setErrorMessage(
          'Sorry, we could not load your module information. Please refresh the page or contact Squad In Touch support.'
        );
      })
      .then(() => {
        setIsLoading(false);
      });
  }, [user]);

  React.useEffect(() => {
    loadModules();
  }, [loadModules]);

  React.useEffect(() => {
    if (pendingRequest && composerRef.current) {
      composerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }

    if (pendingRequest && composerTextareaRef.current) {
      composerTextareaRef.current.focus();
    }
  }, [pendingRequest]);

  const rows = subscriptions
    .filter(sub => ALLOWED_MODULE_KEYS.has(sub.module))
    .map(sub => {
      const cfg = MODULES_CONFIG[sub.module] || {
        title: sub.module,
        description: ''
      };

      let subscriptionDescription = (sub as any).description as string | undefined;

      if ((sub.module === 'CHALLENGES' || sub.module === 'LEAGUES') && sub.includedWithModule === 'SPORT') {
        subscriptionDescription = 'Included for FREE with your Sport module';
      }

      const requests = sub.requests || [];
      const lastRequest = requests[requests.length - 1];
      const hasPendingRequest = !!lastRequest && lastRequest.status === 'PENDING';

      let pendingLabel: string | undefined;
      if (hasPendingRequest) {
        if (lastRequest.type === 'FULL') {
          pendingLabel = 'Your full subscription request is being processed.';
        } else if (lastRequest.type === 'TRIAL') {
          pendingLabel = 'Your free trial request is being processed.';
        } else {
          pendingLabel = 'Your request is being processed.';
        }
      }

      return {
        id: sub.module,
        title: cfg.title,
        description: cfg.description,
        subscriptionDescription,
        statusLabel: buildStatusLabel(sub),
        specialOffer: sub.note || '',
        canRequestTrial: canRequestTrial(sub),
        canRequestActivation: canRequestActivation(sub),
        lastRequestText: getLastRequestText(sub),
        state: getRowState(sub) as RowState,
        hasPendingRequest,
        pendingLabel
      };
    })

    .sort((a, b) => {
      const ia = MODULE_ORDER.indexOf(a.id);
      const ib = MODULE_ORDER.indexOf(b.id);

      const aIndex = ia === -1 ? 999 : ia;
      const bIndex = ib === -1 ? 999 : ib;

      if (aIndex !== bIndex) return aIndex - bIndex;

      return a.title.localeCompare(b.title);
    });

  function startRequest(moduleKey: string, mode: RequestMode, moduleTitle: string) {
    setPendingRequest({ moduleKey, mode, moduleTitle });
    setRequestMessage('');
    setInfoMessage(null);
    setErrorMessage(null);
  }

  async function handleRequest(moduleKey: string, mode: RequestMode, message?: string) {
    setSubmittingModule(moduleKey);
    setErrorMessage(null);
    setInfoMessage(null);

    const requestType: 'TRIAL' | 'FULL' = mode;
    const targetStatus: 'TRIAL' | 'ACTIVE' = mode === 'TRIAL' ? 'TRIAL' : 'ACTIVE';

    try {
      await createModuleSubscriptionRequest(user, moduleKey, requestType, targetStatus, message);

      if (mode === 'TRIAL') {
        const moduleTitle = MODULES_CONFIG[moduleKey]?.title || moduleKey;

        setInfoMessage(
          `Thank you! Your free trial of ${moduleTitle} has been activated. You can start using it straight away – the module status below has been updated.`
        );
      } else {
        setInfoMessage(
          'Thank you! We will get in touch shortly to confirm the details we need for invoicing and setting up Squad In Touch for your school.'
        );
      }

      setPendingRequest(null);
      setRequestMessage('');
      loadModules();
    } catch (e) {
      setErrorMessage(
        'Sorry, something went wrong while sending your request. Please try again or contact Squad In Touch support.'
      );
    } finally {
      setSubmittingModule(null);
    }
  }

  const isInitialLoading = isLoading && !subscriptions.length;
  const activeCount = rows.filter(row => row.statusLabel.kind === 'active').length;
  const trialCount = rows.filter(row => row.statusLabel.kind === 'trial').length;
  const pendingCount = rows.filter(row => row.statusLabel.kind === 'pending' || row.hasPendingRequest).length;
  const inactiveCount = rows.filter(row => row.statusLabel.kind === 'inactive').length;

  return (
    <div className="school-modules">
      <header className="school-modules__header">
        <div className="school-modules__heading">
          <h1>Modules &amp; subscriptions</h1>
          <p>
            Manage the tools available to <strong>{schoolName}</strong>.
          </p>
        </div>

        <div className="school-modules__summary" aria-label="Module subscription summary">
          <span className="school-modules__summary-chip school-modules__summary-chip--active">
            <b>{isInitialLoading ? '—' : activeCount}</b> Active
          </span>
          <span className="school-modules__summary-chip school-modules__summary-chip--trial">
            <b>{isInitialLoading ? '—' : trialCount}</b> Trial
          </span>
          <span className="school-modules__summary-chip school-modules__summary-chip--pending">
            <b>{isInitialLoading ? '—' : pendingCount}</b> Pending
          </span>
          <span className="school-modules__summary-chip school-modules__summary-chip--inactive">
            <b>{isInitialLoading ? '—' : inactiveCount}</b> Not active
          </span>
        </div>

        <a
          href="https://calendly.com/squadintouch"
          target="_blank"
          rel="noopener noreferrer"
          className="school-modules__demo-btn"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="3.5" y="5.5" width="17" height="15" rx="2" />
            <path d="M8 3.5v4M16 3.5v4M3.5 10h17M8 14h3M8 17h6" />
          </svg>
          Book a free demo
        </a>
      </header>

      <div className="school-modules__content">
        {infoMessage && (
          <div className="school-modules__notice school-modules__notice--success" role="status">
            <span aria-hidden="true">✓</span>
            <p>{infoMessage}</p>
            <button type="button" onClick={() => setInfoMessage(null)} aria-label="Dismiss message">
              ×
            </button>
          </div>
        )}
        {errorMessage && subscriptions.length > 0 && (
          <div className="school-modules__notice school-modules__notice--error" role="alert">
            <span aria-hidden="true">!</span>
            <p>{errorMessage}</p>
            <button type="button" onClick={() => setErrorMessage(null)} aria-label="Dismiss message">
              ×
            </button>
          </div>
        )}

        {pendingRequest && (
          <section className="eModuleRequestComposer" ref={composerRef} aria-label="Subscription request">
            <header>
              <span className={pendingRequest.mode === 'TRIAL' ? 'is-trial' : 'is-full'}>
                {pendingRequest.mode === 'TRIAL' ? 'Free trial' : 'Full subscription'}
              </span>
              <div>
                <h2>
                  {pendingRequest.mode === 'TRIAL'
                    ? `Try ${pendingRequest.moduleTitle}`
                    : `Subscribe to ${pendingRequest.moduleTitle}`}
                </h2>
                <p>
                  {pendingRequest.mode === 'TRIAL'
                    ? 'Tell us anything that would help you get the most from your trial.'
                    : 'We’ll contact you to confirm invoicing and initial setup.'}
                </p>
              </div>
            </header>
            <label>
              <span>
                Message <small>optional</small>
              </span>
              <textarea
                rows={3}
                value={requestMessage}
                onChange={event => setRequestMessage(event.target.value)}
                ref={composerTextareaRef}
                placeholder="Add any useful context for the Squad In Touch team"
              />
            </label>
            <footer>
              <button
                type="button"
                className="school-modules__button school-modules__button--secondary"
                onClick={() => {
                  setPendingRequest(null);
                  setRequestMessage('');
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="school-modules__button school-modules__button--primary"
                onClick={() => handleRequest(pendingRequest.moduleKey, pendingRequest.mode, requestMessage)}
                disabled={submittingModule === pendingRequest.moduleKey}
              >
                {submittingModule === pendingRequest.moduleKey ? 'Sending…' : 'Send request'}
              </button>
            </footer>
          </section>
        )}

        <section className="school-modules__panel">
          <header className="school-modules__panel-header">
            <div>
              <div className="school-modules__panel-title">
                <h2>Available modules</h2>
                <span>{isInitialLoading ? '—' : rows.length}</span>
              </div>
              <p>Start a free trial where available or request a full subscription.</p>
            </div>
            {isLoading && subscriptions.length > 0 && (
              <span className="school-modules__updating">
                <i aria-hidden="true" /> Updating
              </span>
            )}
          </header>

          {isInitialLoading ? (
            <div className="school-modules__loading">
              <Loader />
              <span>Loading module information…</span>
            </div>
          ) : errorMessage && rows.length === 0 ? (
            <div className="school-modules__empty school-modules__empty--error">
              <span aria-hidden="true">!</span>
              <h3>Module information could not be loaded</h3>
              <p>Please try again. If the problem continues, contact Squad In Touch support.</p>
              <button type="button" className="school-modules__button school-modules__button--secondary" onClick={loadModules}>
                Try again
              </button>
            </div>
          ) : rows.length === 0 ? (
            <div className="school-modules__empty">
              <span aria-hidden="true">◎</span>
              <h3>No modules available</h3>
              <p>There are no subscription modules configured for this school yet.</p>
            </div>
          ) : (
            <div className="school-modules__grid">
              {rows.map(row => (
                <article key={row.id} className={`school-modules__card school-modules__card--${row.state}`}>
                  <div className="school-modules__card-body">
                    <div>
                      <div className="school-modules__card-top">
                        <h3 className="school-modules__card-title">{row.title}</h3>
                        <span className={`eModuleStatus eModuleStatus--${row.statusLabel.kind}`}>
                          <span className="eModuleStatus__dot" />
                          {row.statusLabel.text}
                        </span>
                      </div>
                      {row.statusLabel.metaLines.map(line => (
                        <div key={line} className="eModuleStatusMeta">
                          {line}
                        </div>
                      ))}
                      {row.subscriptionDescription &&
                        (row.subscriptionDescription.includes('activated once') ? (
                          <div className="school-modules__card-note school-modules__card-note--activation">
                            {row.subscriptionDescription}
                          </div>
                        ) : (
                          <span className="school-modules__card-tag">{row.subscriptionDescription}</span>
                        ))}
                      <p className="school-modules__card-desc">{row.description}</p>
                    </div>

                    {row.specialOffer && <div className="school-modules__card-note">{row.specialOffer}</div>}

                    <div className="school-modules__card-footer">
                      {row.lastRequestText && (
                        <div className="school-modules__card-last-request">{row.lastRequestText}</div>
                      )}
                      {row.pendingLabel && <div className="school-modules__card-pending">{row.pendingLabel}</div>}
                      {(row.canRequestTrial || row.canRequestActivation) && (
                        <div className="school-modules__card-actions">
                          {row.canRequestTrial && (
                            <button
                              type="button"
                              className="school-modules__button school-modules__button--primary school-modules__button--block"
                              onClick={() => startRequest(row.id, 'TRIAL', row.title)}
                              disabled={submittingModule === row.id}
                            >
                              Request free trial
                            </button>
                          )}
                          {row.canRequestActivation && (
                            <button
                              type="button"
                              className="school-modules__button school-modules__button--secondary school-modules__button--block"
                              onClick={() => startRequest(row.id, 'FULL', row.title)}
                              disabled={submittingModule === row.id}
                            >
                              Request full subscription
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
